// --------------------------------------------------------------------------
// Map of CPT page
// --------------------------------------------------------------------------

import React, { useEffect, useRef, useCallback } from 'react';
import {Map, NavigationControl, ScaleControl} from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { TerraDraw, TerraDrawMapLibreGLAdapter, TerraDrawPolygonMode,
  TerraDrawRectangleMode, TerraDrawSelectMode} from "terra-draw";
import { GeoJSON as GeoJSONType } from 'geojson';
import { bbox } from '@turf/turf';
import { Box, IconButton, useToast, ButtonGroup, Tooltip } from "@chakra-ui/react";
import { useCPTContext } from '../../context/cpt_context';
import arrow from "../assets/arrow.png";
import "../styles.css";


const MapCPT: React.FC = React.memo(() => {

  // 1. State, hooks, event handling and helper functions Map
  const toast = useToast();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);
  const { geojsonDataInput, setGeojsonDataInput,
    setErrorInputGeoJSON, mapRef } = useCPTContext();

  // Initialize Map
  useEffect(() => {
    if (!mapRef.current && mapContainerRef.current) {
      mapRef.current = new Map({
        container: mapContainerRef.current,
        style: 'https://tiles.openfreemap.org/styles/bright',
        center: [7.85, 48.00],
        zoom: 6,
      });
      
      // Handle style load errors and fallback to default
      mapRef.current.on('error', (error) => {
        console.error('Error loading custom map style, falling back to default:', error);
        toast({
          title: "Map Provider Error.",
          description: 'Error loading custom map style, falling back to default.',
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        mapRef.current?.setStyle('https://tiles.openfreemap.org/styles/bright');
      });

      // Add scale control
      const scale = new ScaleControl({
        unit: 'metric'
      });
      mapRef.current.addControl(scale, 'bottom-right');

      // Add Navigation Control
      const navControl = new NavigationControl();
      mapRef.current.addControl(navControl, 'bottom-right');

      // Load icons
      mapRef.current.on('style.load', () => {
        if (mapRef.current) {
          mapRef.current.loadImage(arrow).then((response) => {
            if (mapRef.current) {
              if (!mapRef.current.hasImage('arrow-icon')) {
                mapRef.current.addImage('arrow-icon', response.data);
              }
            }
          });
        }
      });

      // Add Draw Control and styling
      mapRef.current.on("style.load", () => {
        if (mapRef.current) {
          const draw = new TerraDraw({
            adapter: new TerraDrawMapLibreGLAdapter({ map: mapRef.current }),
            modes: [
              new TerraDrawRectangleMode({
                styles: {
                  fillColor: "#000000",
                  fillOpacity: 0.5,
                  outlineColor: "#000000",
                  outlineWidth: 1,
                },}),
              new TerraDrawPolygonMode({
                styles: {
                  fillColor: "#000000",
                  fillOpacity: 0.5,
                  outlineColor: "#000000",
                  outlineWidth: 1,
                  closingPointColor: "#000000",
                },}),
              new TerraDrawSelectMode(),
            ],
          });
          drawRef.current = draw;

          // Handle end of drawing and defining the AOI
          drawRef.current.on('finish', () => {
            const features = drawRef.current?.getSnapshot();
            if (features && Array.isArray(features) && features.length > 0) {
              const latestFeature = features[features.length - 1];
              delete latestFeature.id;
              setGeojsonDataInput(latestFeature);
              setErrorInputGeoJSON("");
            }
            // Reset cursor by setting Mode to select and 
            // stop drawing
            drawRef.current?.setMode("select");
            drawRef.current?.stop();
          });
        }
      });
    }

    // Cleanup function to remove the map instance and stop drawing
    return () => {
      if (drawRef.current) {
        drawRef.current.stop();
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null; 
      }
    };
  }, [setGeojsonDataInput, setErrorInputGeoJSON, mapRef, toast]);

  // Handle Rectangle drawing
  const handleRectangleDrawing = () => {
    drawRef.current?.start();
    drawRef.current?.setMode("rectangle");
  }
  
  // Handle Polygon drawing
  const handlePolygonDrawing = () => {
    drawRef.current?.start();
    drawRef.current?.setMode("polygon");
  }

  // Function to add a the AOI layer to the map
  const addAOILayer = useCallback((geoJsonData: GeoJSONType) => {
    if (mapRef.current && mapRef.current.isStyleLoaded()) {

      // Check if one layer already exists and remove it
      if (mapRef.current.getLayer('aoi-layer')) {
        mapRef.current.removeLayer('aoi-layer');
        mapRef.current.removeLayer('aoi-layer-outline');
      }
      if (mapRef.current.getSource('aoi-source')) {
        mapRef.current.removeSource('aoi-source');
      }

      // Define the data to display
      mapRef.current.addSource('aoi-source', {
        type: 'geojson',
        data: geoJsonData,
      });

      // Define how to display the data
      mapRef.current.addLayer({
        id: 'aoi-layer',
        type: 'fill',
        source: 'aoi-source',
        layout: {},
        paint: {
          'fill-color': 'black',
          'fill-opacity': 0.5,
        },
      });
      mapRef.current.addLayer({
        id: 'aoi-layer-outline',
        type: 'line',
        source: 'aoi-source',
        layout: {},
        paint: {
          'line-color': 'black',
          'line-width': 2,
        },
      });
    }
  }, [mapRef]);

  // Add AOI layer to map when geojsonDataInput changes
  useEffect(() => {
    if (!geojsonDataInput) {
      if (mapRef.current && mapRef.current.getLayer('aoi-layer')) {
        mapRef.current.removeLayer('aoi-layer');
        mapRef.current.removeLayer('aoi-layer-outline');
        mapRef.current.removeSource('aoi-source');
      }
    } else {
      addAOILayer(geojsonDataInput);
      // Fit the map to the bounds with padding
      const bounds = bbox(geojsonDataInput);
      if (mapRef.current) {
        const container = mapContainerRef.current;
        if (container){
          const padding = {
            top: container.clientHeight * 0.1,
            bottom: container.clientHeight * 0.1,
            left: container.clientWidth * 0.25,
            right: container.clientWidth * 0.25,
          };
          mapRef.current.fitBounds([[bounds[0], bounds[1]], [bounds[2], bounds[3]]], {
            padding: padding,
            speed: 1.5,
            curve: 1,
          });
        } else {
          console.error('Map container is null.');
        }
      }
    }
  }, [geojsonDataInput, mapRef, addAOILayer]);

  // Icons for the drawing Buttons
  const RectangleIcon = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="black">
        <rect x="5" y="7" width="14" height="10" stroke="black" fill="none" strokeWidth="2" />
        <circle cx="5" cy="7" r="2" fill="black" />
        <circle cx="19" cy="7" r="2" fill="black" />
        <circle cx="5" cy="17" r="2" fill="black" />
        <circle cx="19" cy="17" r="2" fill="black" />
    </svg>
  );
  
  const PolygonIcon = () => (
    <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="black">
        <polygon points="12,4 6,10 8,18 16,18 18,10" stroke="black" fill="none" strokeWidth="2" />
        <circle cx="12" cy="4" r="2" fill="black" />
        <circle cx="6" cy="10" r="2" fill="black" />
        <circle cx="8" cy="18" r="2" fill="black" />
        <circle cx="16" cy="18" r="2" fill="black" />
        <circle cx="18" cy="10" r="2" fill="black" />
    </svg>
  );

  // 2. JSX Map
  return (
    <>
      <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />
      <Box style={{ position: "absolute", top: '10px', right: '10px' }}>
        <ButtonGroup spacing={0} isAttached>
          <Tooltip
            label="Rectangle" aria-label="rectangleDraw"
            placement="bottom" hasArrow bg="Black">
            <IconButton
              aria-label='rectangleDraw'
              icon={<RectangleIcon/>}
              onClick={handleRectangleDrawing}
              bg="white" 
              border="1px" 
              borderColor="gray.300" 
              borderRadius="md" 
              boxShadow="md" 
              _hover={{ bg: "gray.100" }} 
              _active={{ bg: "gray.200" }} 
              _focus={{ boxShadow: "outline" }} 
            />
          </Tooltip>
          <Tooltip
            label="Polygon" aria-label="polygonDraw"
            placement="bottom" hasArrow bg="Black">
            <IconButton
              aria-label='polygonDraw'
              icon={<PolygonIcon />}
              onClick={handlePolygonDrawing}
              bg="white" 
              border="1px" 
              borderColor="gray.300" 
              borderRadius="md" 
              boxShadow="lg" 
              _hover={{ bg: "gray.100" }} 
              _active={{ bg: "gray.200" }} 
              _focus={{ boxShadow: "outline" }} 
              />
          </Tooltip>
        </ButtonGroup>
      </Box>
    </>
  );
});

export default MapCPT;